import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, EnumDecoder, field, IntegerDecoder, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from 'uuid';
import { PaymentMethod, PaymentMethodHelper } from './PaymentMethod.js';
import { upgradePriceFrom2To4DecimalPlaces } from './upgradePriceFrom2To4DecimalPlaces.js';
import { TransferSettings } from './webshops/TransferSettings.js';
export class PayconiqAccount extends AutoEncoder {
    /**
     * Internal reference (update/delete/...)
     */
    id;
    apiKey;
    merchantId = null;
    profileId = null;
    name = null;
    iban = null;
    callbackUrl = null;
    /**
     * Temorary stored whether this payconiq API key is probably still on the old system
     */
    legacyApi = false;
    getDiffName() {
        if (this.name && this.iban) {
            return `${this.name} (${this.iban})`;
        }
        if (this.name) {
            return this.name + ' (Payconiq)';
        }
        if (this.iban) {
            return this.iban + ' (Payconiq)';
        }
        return `Payconiq Account (${this.merchantId ?? this.id})`;
    }
    getDiffValue() {
        // Don't list properties
        return '';
    }
    static get placeholderApiKey() {
        return '••••';
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], PayconiqAccount.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PayconiqAccount.prototype, "apiKey", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PayconiqAccount.prototype, "merchantId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PayconiqAccount.prototype, "profileId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PayconiqAccount.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PayconiqAccount.prototype, "iban", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], PayconiqAccount.prototype, "callbackUrl", void 0);
__decorate([
    field({ decoder: BooleanDecoder, optional: true })
], PayconiqAccount.prototype, "legacyApi", void 0);
/**
 * Should remain private
 */
export class PrivatePaymentConfiguration extends AutoEncoder {
    /**
     * Warning: internal id is used instead of the stripe id
     */
    stripeAccountId = null;
}
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 174 })
], PrivatePaymentConfiguration.prototype, "stripeAccountId", void 0);
export class AdministrationFeeSettings extends AutoEncoder {
    /**
     * In pertenthousand ‱
     * 10 = 0,1% discount
     * 1 = 0,01% discount
     */
    percentage = 0;
    /**
     * In cents
     */
    fixed = 0;
    zeroIfZero = true;
    calculate(price) {
        if (price <= 0 && this.zeroIfZero) {
            return 0;
        }
        return 100 * Math.round(price * this.percentage / 10000_00) + this.fixed; // we round the percentage fee up to 1 cent
    }
    isEqual(other) {
        return this.percentage === other.percentage && this.fixed === other.fixed && this.zeroIfZero === other.zeroIfZero;
    }
    isZero() {
        return this.percentage === 0 && this.fixed === 0;
    }
}
__decorate([
    field({ decoder: IntegerDecoder })
], AdministrationFeeSettings.prototype, "percentage", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], AdministrationFeeSettings.prototype, "fixed", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 228 })
], AdministrationFeeSettings.prototype, "zeroIfZero", void 0);
export class PaymentMethodSettings extends AutoEncoder {
    // Note: these settings are public - don't store sensitive information here
    minimumAmount = 0;
    /**
     * Only show warning if amount is higher than this
     */
    warningAmount = null;
    warningText = '';
    /**
     * Disable this payment method for private users
     */
    companiesOnly = false;
}
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], PaymentMethodSettings.prototype, "minimumAmount", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], PaymentMethodSettings.prototype, "warningAmount", void 0);
__decorate([
    field({ decoder: StringDecoder })
], PaymentMethodSettings.prototype, "warningText", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], PaymentMethodSettings.prototype, "companiesOnly", void 0);
export class PaymentConfiguration extends AutoEncoder {
    transferSettings = TransferSettings.create({});
    paymentMethods = [];
    administrationFee = AdministrationFeeSettings.create({});
    paymentMethodSettings = new Map();
    enableMandates;
    getAvailablePaymentMethods(data) {
        return this.paymentMethods.filter((method) => {
            if (data.forMandate) {
                if (!this.enableMandates) {
                    return false;
                }
                if (!PaymentMethodHelper.canCreateMandate(method)) {
                    return false;
                }
            }
            const settings = this.paymentMethodSettings.get(method);
            if (!settings) {
                // No special settings, always available
                return true;
            }
            if (data.amount < settings.minimumAmount) {
                return false;
            }
            if (settings.companiesOnly && (!data.customer || !data.customer.company)) {
                return false;
            }
            return true;
        });
    }
}
__decorate([
    field({ decoder: TransferSettings })
], PaymentConfiguration.prototype, "transferSettings", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(new EnumDecoder(PaymentMethod)) })
], PaymentConfiguration.prototype, "paymentMethods", void 0);
__decorate([
    field({ decoder: AdministrationFeeSettings })
], PaymentConfiguration.prototype, "administrationFee", void 0);
__decorate([
    field({ decoder: new MapDecoder(new EnumDecoder(PaymentMethod), PaymentMethodSettings), version: 340 })
], PaymentConfiguration.prototype, "paymentMethodSettings", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 398, defaultValue: () => false })
], PaymentConfiguration.prototype, "enableMandates", void 0);
//# sourceMappingURL=PaymentConfiguration.js.map